import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useQuoteExpiry } from "@/shared/hooks/useQuoteExpiry";
import { useTwoFactorAction } from "@/shared/hooks/useTwoFactorAction";
import useTranslations from "@/shared/lang/useTranslations";

import {
  EMPTY_DESTINATION,
  sameDestination,
  type DestinationValues,
} from "../components/swap/destination.schema";
import {
  DEFAULT_SEND_DECIMALS,
  MAX_AUTO_REFRESHES,
  QUOTE_SUBMIT_GUARD_MS,
  SAVED_WALLET_ALIAS_MAX_LENGTH,
} from "../constants/cryptoSwaps.constants";
import { CryptoSwapsService } from "../services/cryptoSwaps.service";
import type { OtcAmountAnchor, OtcPair, OtcSavedWallet } from "../types/cryptoSwaps.types";
import {
  checkCryptoSwapAddress,
  defaultNetwork,
  isSupportedNetwork,
  type AddressCheckStatus,
  defaultWalletAlias,
} from "../utils/cryptoSwapAddress";
import {
  clientAmountIssue,
  serverAmountIssue,
  type AmountIssue,
  type AmountIssueContext,
} from "../utils/cryptoSwapAmountIssues";
import { canonicalAmount, exceedsBalance, type AmountBounds } from "../utils/cryptoSwapAmounts";
import { converterField, debitDiffersFromTyped } from "../utils/cryptoSwapConverter";
import {
  isRangeRejection,
  normalizeCryptoSwapError,
  QUOTE_LAPSED_ERROR,
  type NormalizedCryptoSwapError,
} from "../utils/cryptoSwapErrors";
import { amountGate, gatedQuoteAmount, receiveBoundsOf, sendBoundsOf } from "../utils/cryptoSwapLimits";
import { cryptoSwapOrderPath } from "../utils/cryptoSwapLinks";
import { isNewQuoteRequest, type RequestedQuote } from "../utils/cryptoSwapQuoteScheduler";
import {
  quoteDeadlineMs,
  resolveCryptoSwapBlock,
  resolveSubmitBlock,
  type CryptoSwapBlock,
} from "../utils/cryptoSwapStatus";
import { useCryptoSwapQuote } from "./useCryptoSwapQuote";

export type CryptoSwapSubmitPhase = "idle" | "creating" | "failed";

/**
 * Why the destination blocks the review: the address check, a concept over the length cap, or the save
 * details.
 */
export type DestinationIssue =
  | Exclude<AddressCheckStatus, "valid">
  | "concept"
  | "save_holder"
  | "save_alias";

export interface UseCryptoSwapFormOptions {
  pairs: OtcPair[];
  canWrite: boolean;
  /** INTERNAL staff act on behalf of the organization they pick; members are pinned server-side. */
  isStaff: boolean;
  organizationId: string | null;
  /** `balance_available` of the send-currency wallet, or null while unknown. */
  availableBalance: string | null;
  /** The organization has no wallet in the send currency. */
  walletMissing: boolean;
  /** A wallet saved on the way to the order, so the list shows it next time. */
  onWalletSaved?: (wallet: OtcSavedWallet) => void;
  initial?: { anchor: OtcAmountAnchor; amount: string; network: string };
}

export function useCryptoSwapForm({
  pairs,
  canWrite,
  isStaff,
  organizationId,
  availableBalance,
  walletMissing,
  onWalletSaved,
  initial,
}: UseCryptoSwapFormOptions) {
  const { t } = useTranslations("cryptoSwaps");
  const navigate = useNavigate();
  const saveWalletTwoFactor = useTwoFactorAction<OtcSavedWallet>();
  const { start: startSaveWallet } = saveWalletTwoFactor;

  const [selectedPairKey, setSelectedPairKey] = useState("");
  const [chosenNetwork, setChosenNetwork] = useState(initial?.network ?? "");
  const [anchor, setAnchor] = useState<OtcAmountAnchor>(initial?.anchor ?? "send");
  const [amount, setAmount] = useState(initial?.amount ?? "");
  const [destination, setDestinationState] = useState<DestinationValues>(EMPTY_DESTINATION);
  const [destinationValid, setDestinationValid] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  /** The quote id the rules were acknowledged for: a new price must be read and acknowledged again. */
  const [acknowledgedFor, setAcknowledgedFor] = useState<string | null>(null);
  const [submitPhase, setSubmitPhase] = useState<CryptoSwapSubmitPhase>("idle");
  const [submitError, setSubmitError] = useState<NormalizedCryptoSwapError | null>(null);
  /** The deadline the countdown fired for, tagged with its quote so a throttled timer cannot un-expire it. */
  const [expiredQuote, setExpiredQuote] = useState<{ id: string; atMs: number } | null>(null);
  const [manualTick, requestManualQuote] = useReducer((tick: number) => tick + 1, 0);

  const quote = useCryptoSwapQuote();

  const pair = useMemo<OtcPair | null>(
    () => pairs.find((candidate) => candidate.pair === selectedPairKey) ?? pairs[0] ?? null,
    [pairs, selectedPairKey],
  );
  const networks = useMemo(() => (pair?.networks ?? []).filter(isSupportedNetwork), [pair]);
  const network = networks.includes(chosenNetwork) ? chosenNetwork : defaultNetwork(networks);
  const organizationReady = !isStaff || !!organizationId;
  const pairAvailable = !!pair && pair.available && !!network;

  const sendDecimals = pair?.send_decimals ?? DEFAULT_SEND_DECIMALS;
  const receiveDecimals = pair?.receive_decimals ?? 2;
  const sendBounds = useMemo<AmountBounds | null>(() => sendBoundsOf(pair), [pair]);
  const receiveBounds = useMemo<AmountBounds | null>(() => receiveBoundsOf(pair), [pair]);

  const gate = useMemo(() => {
    const input = { anchor, amount, sendDecimals, receiveDecimals, sendBounds, receiveBounds };
    return { check: amountGate(input), payload: gatedQuoteAmount(input) };
  }, [anchor, amount, sendDecimals, receiveDecimals, sendBounds, receiveBounds]);
  const amountCheck = gate.check;
  const gatedAmount = gate.payload;
  const canQuote = !!pair && organizationReady && pairAvailable && gatedAmount !== null;

  const formSignature = useMemo(
    () => [pair?.pair ?? "", organizationId ?? "-", network, anchor, canonicalAmount(amount)].join("~"),
    [pair, organizationId, network, anchor, amount],
  );

  const { requestQuote, clearQuote, isManualMode } = quote;
  /** One-shot: a keystroke deserves the debounce, a deliberate click does not. */
  const immediateRef = useRef(false);
  const servedManualTickRef = useRef(0);
  const requestedRef = useRef<RequestedQuote | null>(null);
  const autoRequotedForRef = useRef<string | null>(null);
  const autoRefreshesRef = useRef(0);
  /** A wallet saved by a submit that did not reach the order, reused instead of being saved twice. */
  const savedWalletRef = useRef<{ network: string; address: string; id: string } | null>(null);

  useEffect(() => {
    if (!canQuote || !pair) {
      requestedRef.current = null;
      clearQuote();
      return;
    }
    if (isManualMode && manualTick === servedManualTickRef.current) return;
    servedManualTickRef.current = manualTick;
    const requested = requestedRef.current;
    if (!isNewQuoteRequest(requested, formSignature, manualTick)) return;
    if (requested?.signature !== formSignature) autoRefreshesRef.current = 0;
    requestedRef.current = { signature: formSignature, tick: manualTick };

    const immediate = immediateRef.current;
    immediateRef.current = false;
    requestQuote(
      {
        pair: pair.pair,
        ...gatedAmount,
        network,
        ...(isStaff && organizationId ? { organization_id: organizationId } : {}),
      },
      { immediate, signature: formSignature },
    );
  }, [
    canQuote,
    gatedAmount,
    formSignature,
    isManualMode,
    manualTick,
    requestQuote,
    clearQuote,
    pair,
    network,
    isStaff,
    organizationId,
  ]);

  const requestPrice = useCallback(() => {
    immediateRef.current = true;
    requestManualQuote();
  }, []);

  const priceNow = useCallback(() => {
    autoRefreshesRef.current = 0;
    requestPrice();
  }, [requestPrice]);

  const currentQuote = quote.quote;
  const currentQuoteRef = useRef(currentQuote);
  useEffect(() => {
    currentQuoteRef.current = currentQuote;
  }, [currentQuote]);
  const deadlineMs = quoteDeadlineMs(currentQuote);
  const deadlineIso = useMemo(
    () => (deadlineMs === null ? null : new Date(deadlineMs).toISOString()),
    [deadlineMs],
  );

  const handleQuoteExpired = useCallback(() => {
    if (!currentQuote || deadlineMs === null) return;
    setExpiredQuote({ id: currentQuote.id, atMs: deadlineMs });
    // Once per quote, for a valid amount only; never under an open review, a hidden tab or an idle streak.
    if (reviewOpen || submitPhase === "creating" || quote.isPricing || quote.isManualMode || !canQuote)
      return;
    if (autoRequotedForRef.current === currentQuote.id) return;
    if (autoRefreshesRef.current >= MAX_AUTO_REFRESHES || document.visibilityState === "hidden") return;
    autoRequotedForRef.current = currentQuote.id;
    autoRefreshesRef.current += 1;
    requestPrice();
  }, [
    currentQuote,
    deadlineMs,
    reviewOpen,
    submitPhase,
    quote.isPricing,
    quote.isManualMode,
    canQuote,
    requestPrice,
  ]);

  useQuoteExpiry(deadlineIso, handleQuoteExpired, quote.clockOffsetMs);

  const isFresh = !!currentQuote && !quote.isPricing && quote.quoteSignature === formSignature;
  const quoteExceedsBalance = exceedsBalance(currentQuote?.send.amount ?? null, availableBalance);
  const amountRejected = isRangeRejection(quote.error);

  const block = useMemo<CryptoSwapBlock>(
    () =>
      resolveCryptoSwapBlock({
        canWrite,
        organizationReady,
        pairAvailable,
        amountCheck,
        quote: currentQuote,
        isPricing: quote.isPricing,
        amountRejected,
        quoteSignature: quote.quoteSignature,
        formSignature,
        now: Math.max(
          Date.now() + quote.clockOffsetMs,
          expiredQuote && expiredQuote.id === currentQuote?.id ? expiredQuote.atMs : 0,
        ),
        walletMissing,
        exceedsBalance: quoteExceedsBalance,
        destinationValid,
      }),
    [
      canWrite,
      organizationReady,
      pairAvailable,
      amountCheck,
      currentQuote,
      quote.isPricing,
      amountRejected,
      quote.quoteSignature,
      quote.clockOffsetMs,
      formSignature,
      expiredQuote,
      walletMissing,
      quoteExceedsBalance,
      destinationValid,
    ],
  );
  const acknowledged = !!currentQuote && acknowledgedFor === currentQuote.id;
  const submitBlock = resolveSubmitBlock(block, acknowledged);

  const issueContext: AmountIssueContext | null = pair
    ? {
        side: anchor,
        sendCurrency: pair.send_currency,
        receiveCurrency: pair.receive_currency,
        sendDecimals,
        receiveDecimals,
        sendBounds,
        receiveBounds,
      }
    : null;
  const anchorIssue = issueContext
    ? (clientAmountIssue(amountCheck, issueContext) ??
      (quote.isPricing ? null : serverAmountIssue(quote.error, issueContext)))
    : null;
  const balanceIssue: AmountIssue | null = !pair
    ? null
    : walletMissing
      ? { kind: "no_wallet", side: "send", currency: pair.send_currency }
      : isFresh && quoteExceedsBalance && availableBalance
        ? { kind: "balance", side: "send", available: availableBalance, currency: pair.send_currency }
        : null;

  const noWallet = balanceIssue?.kind === "no_wallet";
  const sendIssue = noWallet || anchor !== "send" ? balanceIssue : (anchorIssue ?? balanceIssue);
  const receiveIssue = anchor === "receive" ? anchorIssue : null;
  const reviewIssue = block === "balance" ? balanceIssue : block === "amount" ? anchorIssue : null;

  const addressStatus = checkCryptoSwapAddress(network, destination.address).status;
  const destinationIssue: DestinationIssue | null = destinationValid
    ? null
    : addressStatus !== "valid"
      ? addressStatus
      : destination.save &&
          !destination.savedWalletId &&
          destination.alias.trim().length > SAVED_WALLET_ALIAS_MAX_LENGTH
        ? "save_alias"
        : destination.save &&
            !destination.savedWalletId &&
            destination.ownership === "THIRD_PARTY" &&
            !destination.holderName.trim()
          ? "save_holder"
          : "concept";

  const converter = { anchor, amount, quote: currentQuote, isPricing: quote.isPricing };
  const sendField = converterField("send", converter);
  const receiveField = converterField("receive", converter);

  const setSendAmount = useCallback((value: string) => {
    setAnchor("send");
    setAmount(value);
  }, []);

  // A suggested amount is a decision already made: price it now rather than after the typing debounce.
  const applySuggestedAmount = useCallback((value: string) => {
    immediateRef.current = true;
    setAnchor("send");
    setAmount(value);
  }, []);

  const setReceiveAmount = useCallback((value: string) => {
    setAnchor("receive");
    setAmount(value);
  }, []);

  const selectPair = useCallback((key: string) => {
    setSelectedPairKey(key);
    immediateRef.current = true;
  }, []);

  const selectNetwork = useCallback((code: string) => {
    setChosenNetwork(code);
    immediateRef.current = true;
  }, []);

  const setDestination = useCallback((values: DestinationValues, valid: boolean) => {
    setDestinationState((previous) => (sameDestination(previous, values) ? previous : values));
    setDestinationValid(valid);
  }, []);

  const currentQuoteId = currentQuote?.id ?? null;
  const setAcknowledged = useCallback(
    (value: boolean) => setAcknowledgedFor(value ? currentQuoteId : null),
    [currentQuoteId],
  );

  const openReview = useCallback(() => {
    if (block !== null) return;
    setAcknowledgedFor(null);
    setSubmitError(null);
    setSubmitPhase("idle");
    setReviewOpen(true);
  }, [block]);

  const closeReview = useCallback(() => {
    if (submitPhase === "creating") return;
    setReviewOpen(false);
    setSubmitError(null);
    setSubmitPhase("idle");
    // The expiry was held back while the review was open; the form never sits on a dead price.
    if (block === "expired" && currentQuote && !quote.isManualMode) {
      autoRequotedForRef.current = currentQuote.id;
      priceNow();
    }
  }, [submitPhase, block, currentQuote, quote.isManualMode, priceNow]);

  const submit = useCallback(async () => {
    const idempotencyKey = quote.idempotencyKey;
    if (submitBlock !== null || !currentQuote || !idempotencyKey || !network) return;

    setSubmitError(null);
    const concept = destination.concept.trim();
    const address = destination.address.trim();
    const reused = savedWalletRef.current;
    let savedWalletId =
      destination.savedWalletId ||
      (destination.save && reused?.network === network && reused.address === address ? reused.id : "");
    if (!savedWalletId && destination.save) {
      const holderName = destination.holderName.trim();
      try {
        const saved = await startSaveWallet((code) =>
          CryptoSwapsService.createSavedWallet(
            {
              network,
              address,
              alias:
                destination.alias.trim() ||
                defaultWalletAlias(address, (ends) => t("saved_wallet_default_alias", ends)),
              ownership: destination.ownership,
              ...(destination.ownership === "THIRD_PARTY" ? { holder_name: holderName } : {}),
              ...(isStaff && organizationId ? { organization_id: organizationId } : {}),
            },
            code,
          ),
        );
        // Cancelled, or the person still has to enrol: nothing was bought.
        if (!saved) return;
        savedWalletId = saved.id;
        savedWalletRef.current = { network, address, id: saved.id };
        onWalletSaved?.(saved);
      } catch (caught) {
        setSubmitError(normalizeCryptoSwapError(caught, "orders_create"));
        setSubmitPhase("failed");
        return;
      }
      // The second factor can outlast the price: never redeem a quote that lapsed or was replaced meanwhile.
      if (currentQuoteRef.current?.id !== currentQuote.id) return;
      const deadline = quoteDeadlineMs(currentQuote);
      if (deadline === null || deadline <= Date.now() + quote.clockOffsetMs) {
        setSubmitError(QUOTE_LAPSED_ERROR);
        setSubmitPhase("failed");
        clearQuote();
        priceNow();
        return;
      }
    }

    setSubmitPhase("creating");
    try {
      const order = await CryptoSwapsService.createOrderOnce({
        quote_id: currentQuote.id,
        network,
        ...(savedWalletId ? { saved_wallet_id: savedWalletId } : { destination_address: address }),
        idempotency_key: idempotencyKey,
        ...(concept ? { concept } : {}),
        ...(isStaff && organizationId ? { organization_id: organizationId } : {}),
      });
      navigate(cryptoSwapOrderPath(order.id));
    } catch (caught) {
      const expiresAt = Date.parse(currentQuote.expires_at);
      const quoteExpired =
        Number.isFinite(expiresAt) && expiresAt - (Date.now() + quote.clockOffsetMs) <= QUOTE_SUBMIT_GUARD_MS;
      const normalized = normalizeCryptoSwapError(caught, "orders_create", { quoteExpired });
      setSubmitError(normalized);
      setSubmitPhase("failed");
      if (normalized.kind === "quote_expired") {
        clearQuote();
        priceNow();
      }
    }
  }, [
    quote.idempotencyKey,
    quote.clockOffsetMs,
    submitBlock,
    currentQuote,
    network,
    destination,
    isStaff,
    organizationId,
    navigate,
    clearQuote,
    priceNow,
    startSaveWallet,
    onWalletSaved,
  ]);

  // One submission at a time: a second click during the save-wallet 2FA or the order call is ignored.
  const submitting = useRef(false);
  const submitOnce = useCallback(async () => {
    if (submitting.current) return;
    submitting.current = true;
    try {
      await submit();
    } finally {
      submitting.current = false;
    }
  }, [submit]);
  return {
    saveWalletTwoFactor,
    pair,
    pairs,
    selectPair,
    networks,
    network,
    selectNetwork,
    organizationReady,
    pairAvailable,
    anchor,
    amount,
    setSendAmount,
    setReceiveAmount,
    applySuggestedAmount,
    sendField,
    receiveField,
    sendDecimals,
    receiveDecimals,
    receiveBounds,
    sendBounds,
    sendIssue,
    receiveIssue,
    reviewIssue,
    debitDiffers: isFresh && debitDiffersFromTyped(anchor, amount, currentQuote),
    destination,
    destinationValid,
    destinationIssue,
    setDestination,
    quote: currentQuote,
    isPricing: quote.isPricing,
    quoteError: quote.error,
    amountRejected,
    isManualMode: quote.isManualMode,
    clockOffsetMs: quote.clockOffsetMs,
    priceNow,
    block,
    submitBlock,
    reviewOpen,
    openReview,
    closeReview,
    acknowledged,
    setAcknowledged,
    submitPhase,
    submitError,
    submit: submitOnce,
  };
}

export type UseCryptoSwapFormResult = ReturnType<typeof useCryptoSwapForm>;
